import { clipMotionPreviewStyle } from '@/lib/videoMotionPreview';
import { normalizeMotionPreset } from '@/lib/videoMotionPresets';
import { applyPreviewLayerStylesToElement, mergePreviewLayerStyles } from '@/lib/videoTransitionPreview';

function transitionMediaElement(videoRef, imageRef) {
  return videoRef?.current || imageRef?.current || null;
}

/**
 * Apply Ken Burns / motion transform imperatively (60fps, no React style commits).
 */
export function applyPreviewMotionFrame({
  layerRef,
  getTransportTime,
  clipRef,
  animationEnabledRef,
  filterStyleRef,
  extraLayersRef,
  motionOptsRef,
}) {
  const clip = clipRef?.current;
  const layer = layerRef?.current;
  if (!clip || !layer || !animationEnabledRef?.current) return;

  const preset = normalizeMotionPreset(clip.motionPreset);
  if (preset === 'none' && !clip?.audioMotionTimeline?.keyframes?.length) return;

  const t = getTransportTime?.() ?? 0;
  const extras = extraLayersRef?.current || [];
  const motionOpts = motionOptsRef?.current || {};
  applyPreviewLayerStylesToElement(
    layer,
    mergePreviewLayerStyles(
      filterStyleRef?.current,
      clipMotionPreviewStyle(clip, t, motionOpts),
      ...extras,
    ),
  );
}

/**
 * Imperatively update dual-layer transition preview media (60fps during playback).
 */
export function applyTransitionPreviewFrame({
  blend,
  timelineTime,
  buildClipVisualStyle,
  outgoingVideoRef,
  outgoingImageRef,
  incomingVideoRef,
  incomingImageRef,
}) {
  if (!blend || typeof buildClipVisualStyle !== 'function') return;

  const { outgoing, incoming, layerStyles, handoff } = blend;
  const outgoingEl = transitionMediaElement(outgoingVideoRef, outgoingImageRef);
  if (outgoingEl) {
    applyPreviewLayerStylesToElement(
      outgoingEl,
      mergePreviewLayerStyles(
        buildClipVisualStyle(outgoing, timelineTime),
        layerStyles?.outgoing,
      ),
    );
  }

  const incomingEl = transitionMediaElement(incomingVideoRef, incomingImageRef);
  if (incomingEl) {
    applyPreviewLayerStylesToElement(
      incomingEl,
      mergePreviewLayerStyles(
        buildClipVisualStyle(incoming, handoff),
        layerStyles?.incoming,
      ),
    );
  }
}

/**
 * Imperatively update the main preview layer during playback (motion and/or transition-on-main).
 */
export function applyMainClipPreviewFrame({
  layerRef,
  getTransportTime,
  clipRef,
  animationEnabledRef,
  filterStyleRef,
  extraLayersRef,
  motionOptsRef,
  blend,
  outgoingDrivesMain,
  buildClipVisualStyle,
}) {
  const clip = clipRef?.current;
  const layer = layerRef?.current;
  if (!clip || !layer) return;

  const preset = normalizeMotionPreset(clip.motionPreset);
  const motionActive = Boolean(
    animationEnabledRef?.current
    && (preset !== 'none' || clip?.audioMotionTimeline?.keyframes?.length),
  );

  if (motionActive) {
    applyPreviewMotionFrame({
      layerRef,
      getTransportTime,
      clipRef,
      animationEnabledRef,
      filterStyleRef,
      extraLayersRef,
      motionOptsRef,
    });
    return;
  }

  if (outgoingDrivesMain && blend?.layerStyles?.outgoing) {
    const t = getTransportTime?.() ?? 0;
    const base = typeof buildClipVisualStyle === 'function'
      ? buildClipVisualStyle(clip, t)
      : (filterStyleRef?.current || {});
    applyPreviewLayerStylesToElement(
      layer,
      mergePreviewLayerStyles(base, blend.layerStyles.outgoing),
    );
  }
}
